import { InfoOutlined as InfoOutlinedIcon } from '@mui/icons-material';
import { Autocomplete, autocompleteClasses, Button, createFilterOptions, IconButton, InputAdornment, MenuItem, Stack, TextField, Tooltip } from '@mui/material';
import React, { useCallback, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { CampaignBuilderDialog } from 'src/modules/campaign-builder-v2/components/DynamicCampaignBuilder/DynamicCampaignBuilder';
import { buildDefaultContactCaptureName, CONTACT_CAPTURE_TYPE_LABELS, CONTACT_CAPTURE_TYPES } from '../../helpers';
import { Campaign } from 'src/modules/campaign-builder-v2/types';
import { ContactCaptureCampaign, ContactCaptureType } from '../../types';
import { useBuilder } from './ContactCaptureBuilderContext';
import { CreateCampaignOption } from './CreateCampaignOption.styles';

const CREATE_CAMPAIGN = { create: true } as const;
type CampaignOption = ContactCaptureCampaign | typeof CREATE_CAMPAIGN;
const isCreateOption = (option: CampaignOption): option is typeof CREATE_CAMPAIGN => 'create' in option;
const filterCampaigns = createFilterOptions<ContactCaptureCampaign>();
const keepBelowCreateOption = (listbox: HTMLUListElement) => {
  const createOption = listbox.querySelector<HTMLElement>('[data-option-index="0"]');
  const highlighted = listbox.querySelector<HTMLElement>(`.${autocompleteClasses.focused}`);
  if (!createOption || !highlighted || highlighted === createOption) return;
  const visibleTop = listbox.scrollTop + createOption.offsetHeight;
  if (highlighted.offsetTop < visibleTop) listbox.scrollTop = highlighted.offsetTop - createOption.offsetHeight;
};

const formTypeHelperText = (formTypeLocked: boolean, type: ContactCaptureType) => {
  if (formTypeLocked) return 'Form type cannot be changed after publishing.';
  return type === 'popup'
    ? 'Display the form as a website popup.'
    : 'Embed the form directly in a page.';
};

const DOMAIN_RULES = (
  <>
    Enter the address without a page path or port.
    <br />
    <b>example.com</b> also covers www.example.com, shop.example.com and any other subdomain.
    <br />
    <b>https://example.com</b> allows only that exact address.
  </>
);

export const ContactCaptureBasicsSection = () => {
  const {
    availableCampaigns,
    busy,
    addCampaign,
    controller,
    retryCampaigns,
    unavailableCampaign,
    domain,
    domainError,
    formTypeLocked,
    getValidationError,
    campaign,
    campaignsError,
    campaignsLoading,
    name,
    setCampaign,
    setName,
    setDomain,
    setSettings,
    setType,
    settings,
    type,
  } = useBuilder();

  const [createOpen, setCreateOpen] = useState(false);
  const listboxRef = useRef<HTMLUListElement | null>(null);
  const openListAtTop = useCallback((node: HTMLUListElement | null) => {
    listboxRef.current = node;
    if (node) node.scrollTop = 0;
  }, []);
  const handleHighlightChange = useCallback((_: React.SyntheticEvent, __: CampaignOption | null, reason: string) => {
    if (reason === 'keyboard') queueMicrotask(() => listboxRef.current && keepBelowCreateOption(listboxRef.current));
  }, []);
  // Stable, so the embedded builder is not rebuilt on every render of this form.
  const closeCreate = useCallback(() => setCreateOpen(false), []);
  const handleCreated = useCallback((created: Campaign) => {
    if (addCampaign(created)) toast.success('Join campaign created and selected.');
    else toast('Campaign saved as a draft. Activate it before selecting it for contact capture.');
    setCreateOpen(false);
  }, [addCampaign]);
  const campaignError = campaignsError
    || (unavailableCampaign ? 'The selected campaign is no longer an active join campaign. Choose another campaign.' : null)
    || getValidationError('Campaign')?.message;

  return (
    <Stack spacing={2.5}>
      <TextField
        disabled={busy}
        label="Name"
        value={name}
        onChange={event => setName(event.target.value)}
        error={Boolean(getValidationError('Name'))}
        helperText={getValidationError('Name')?.message}
        required
        fullWidth
        inputProps={{ 'aria-label': 'Contact capture name' }}
      />
      <TextField
        select
        label="Form type"
        value={type}
        onChange={event => setType(event.target.value as ContactCaptureType)}
        disabled={busy || formTypeLocked}
        helperText={formTypeHelperText(formTypeLocked, type)}
        fullWidth
      >
        {CONTACT_CAPTURE_TYPES.map(value => (
          <MenuItem key={value} value={value}>{CONTACT_CAPTURE_TYPE_LABELS[value]}</MenuItem>
        ))}
      </TextField>
      <TextField
        disabled={busy}
        label="Website domain"
        value={domain}
        onChange={event => setDomain(event.target.value)}
        placeholder="https://example.com"
        error={Boolean(domainError)}
        helperText={domainError || 'The form only works on this website, so other sites can\'t embed it.'}
        required
        fullWidth
        inputProps={{ 'aria-label': 'Website domain', autoCapitalize: 'none', spellCheck: false }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <Tooltip title={DOMAIN_RULES}>
                <IconButton size="small" edge="end" aria-label="How the website domain is matched">
                  <InfoOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            </InputAdornment>
          ),
        }}
      />
      {type === 'popup' && (
        <TextField
          disabled={busy}
          type="number"
          label="Popup delay"
          // A string, so React rewrites the input: a numeric value equal to "030" leaves the leading zero.
          value={String(settings.behavior.popupDelaySeconds)}
          onChange={event => setSettings(current => ({
            ...current,
            behavior: {
              ...current.behavior,
              popupDelaySeconds: Math.max(0, Math.min(300, Number(event.target.value))),
            },
          }))}
          inputProps={{ min: 0, max: 300 }}
          helperText="Seconds to wait before showing the popup (0–300)."
          fullWidth
        />
      )}
      <Stack spacing={0.5}>
        <Autocomplete<CampaignOption>
          options={[CREATE_CAMPAIGN, ...availableCampaigns]}
          value={campaign}
          onChange={(_, selected) => {
            if (selected && isCreateOption(selected)) setCreateOpen(true);
            else setCampaign(selected);
          }}
          filterOptions={(options, state) => [
            CREATE_CAMPAIGN,
            ...filterCampaigns(options.filter((option): option is ContactCaptureCampaign => !isCreateOption(option)), state),
          ]}
          getOptionLabel={option => (isCreateOption(option) ? '' : option.name)}
          // Campaign names are not unique; the default key is the label.
          getOptionKey={option => (isCreateOption(option) ? 'create' : option.id)}
          getOptionDisabled={option => isCreateOption(option) && !controller.companyId}
          isOptionEqualToValue={(option, value) => !isCreateOption(option) && !isCreateOption(value) && option.id === value.id}
          renderOption={({ key, ...props }, option) => (isCreateOption(option)
            ? <CreateCampaignOption key={key} {...props}>+ Create new join campaign</CreateCampaignOption>
            : <li key={key} {...props}>{option.name}</li>)}
          ListboxProps={{ ref: openListAtTop }}
          onHighlightChange={handleHighlightChange}
          disabled={busy || campaignsLoading}
          renderInput={params => (
            <TextField
              {...params}
              label="Add to Campaign"
              placeholder="Search active join campaigns"
              required
              error={Boolean(campaignError)}
              helperText={campaignsLoading
                ? 'Loading join campaigns…'
                : campaignError || 'New signups will join the selected campaign and associated group(s).'}
            />
          )}
        />
        {campaignsError && <Button onClick={retryCampaigns} disabled={busy} sx={{ alignSelf: 'flex-start' }}>Retry loading campaigns</Button>}
      </Stack>
      {createOpen && (
        <CampaignBuilderDialog
          key={controller.companyId}
          onClose={closeCreate}
          onSaved={handleCreated}
          initialName={buildDefaultContactCaptureName()}
        />
      )}
    </Stack>
  );
};
